<template>
  <v-dialog persistent max-width="800px" v-model="dialog" fullscreen transition="dialog-bottom-transition">
    <div class="course-edit-wrapper">
      <v-app-bar app color="white" flat class="app-bar-border">
        <v-btn icon @click="closeDialog()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title v-if="courseEdit" class="font-weight-bold"> Kurs bearbeiten </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn text color="primary" @click="showPushPopupMethod()">
          <v-icon left>mdi-check</v-icon>
          Speichern
        </v-btn>
      </v-app-bar>

      <v-container v-if="!courseEdit" class="d-flex justify-center align-center" style="height: 100vh">
        <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
      </v-container>

      <v-container v-else class="form-container">
        <div class="header-image-container">
          <v-img :src="headerImageSrc" class="header-image" height="250px" gradient="to top right, rgba(0,0,0,.3), rgba(25,32,72,.7)">
            <v-row class="fill-height ma-0" align="end" justify="start">
              <v-card-title class="white--text text-h5 font-weight-bold">
                {{ courseEdit.course.name }}
              </v-card-title>
            </v-row>
          </v-img>
        </div>

        <v-row>
          <v-col cols="12">
            <v-card class="form-card" outlined>
              <v-card-title>Grundlagen</v-card-title>
              <v-card-text>
                <course-search :value="courseEdit.course" label="Kursformat" rights="gf_c_plan" @update="updateCourse" outlined></course-search>
                <v-select
                  v-model="courseEdit.label"
                  :items="labels"
                  item-text="text"
                  item-value="_id"
                  label="Label (optional)"
                  outlined
                  class="mt-4"
                  clearable></v-select>
                <v-select
                  v-if="events.length"
                  v-model="courseEdit.event"
                  :items="events"
                  item-text="title"
                  item-value="_id"
                  label="Event (optional)"
                  outlined
                  class="mt-4"
                  clearable></v-select>
                <v-text-field
                  v-model="courseEdit.additionalInfo"
                  outlined
                  label="Zusatzinfo (optional)"
                  placeholder="z.B. Mindest-Teilnehmerzahl"></v-text-field>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card class="form-card" outlined>
              <v-card-title>Zeit, Ort & Teilnehmer</v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="6"><v-text-field v-model="time" type="time" outlined label="Uhrzeit"></v-text-field></v-col>
                  <v-col cols="6"><v-text-field v-model="courseEdit.duration" type="number" outlined label="Dauer (Min)"></v-text-field></v-col>
                </v-row>
                <v-select v-model="courseEdit.room" :items="rooms" item-text="name" item-value="_id" outlined label="Raum"></v-select>
                <v-switch v-model="courseEdit.reqSignup" label="Anmeldung erforderlich" class="mt-4"></v-switch>
                <v-text-field v-if="courseEdit.reqSignup" v-model="courseEdit.amount" type="number" outlined label="Anzahl Plätze"></v-text-field>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card class="form-card" outlined>
              <v-card-title>Kursleiter & Darstellung</v-card-title>
              <v-card-text>
                <div v-for="(instructor, index) in courseEdit.instructors" :key="index" class="d-flex align-center">
                  <user-search
                    :key="index + instructor + '-inst'"
                    instructor
                    :value="instructor"
                    label="Instruktor"
                    rights="gf_c_plan"
                    @update="(newVal) => updateInstructor(newVal, instructor)"
                    outlined
                    class="flex-grow-1"></user-search>
                  <v-btn icon color="grey" @click="removeInstructor(instructor)" class="ml-2"><v-icon>mdi-delete</v-icon></v-btn>
                </div>
                <v-btn text color="primary" @click="addInstructor" class="mt-2"><v-icon left>mdi-plus</v-icon> Instruktor hinzufügen</v-btn>
                <v-divider class="my-6"></v-divider>
                <v-subheader>Kurs-Icon</v-subheader>
                <v-chip-group v-model="courseEdit.icon" column active-class="primary">
                  <v-chip large v-for="ic in icons" :key="ic" :value="ic" filter>
                    <v-avatar class="iconAvatar" center>
                      <lottie-player mode="normal" style="width: 60px; height: 60px" :src="'/api/gf/icons/' + ic"></lottie-player>
                    </v-avatar>
                  </v-chip>
                </v-chip-group>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card class="form-card" outlined style="border-color: #d32f2f">
              <v-card-title class="red--text text--darken-2">Kurs-Status</v-card-title>
              <v-card-text>
                <p class="body-1">Sage diesen einzelnen Kurs ab oder reaktiviere ihn wieder. Die Kursplanung bleibt davon unberührt.</p>
                <v-btn
                  block
                  large
                  :dark="!courseEdit.cancelled"
                  :outlined="courseEdit.cancelled"
                  :color="courseEdit.cancelled ? 'grey' : 'red darken-2'"
                  @click="changeCourseActivation">
                  <v-icon left>{{ courseEdit.cancelled ? "mdi-check-circle-outline" : "mdi-cancel" }}</v-icon>
                  {{ courseActivationText }}
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <v-dialog v-model="showCancelledPopup" persistent max-width="400px">
        <v-card>
          <v-card-title class="text-h5">Sichtbarkeit des abgesagten Kurses</v-card-title>
          <v-card-text>Soll dieser Kurs für die Kunden in der Kursliste weiterhin sichtbar bleiben (als "abgesagt" markiert)?</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="cancelledCourseVisible(false)">Nein, ausblenden</v-btn>
            <v-btn color="primary" @click="cancelledCourseVisible(true)">Ja, anzeigen</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="showPushPopUp" persistent max-width="400px">
        <v-card>
          <v-card-text>Möchtest du die bereits angemeldeten Teilnehmer per Push-Nachricht über die Änderungen informieren?</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="saveUpdatedCourse(false)">Nein</v-btn>
            <v-btn color="primary" @click="saveUpdatedCourse(true)">Ja, benachrichtigen</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- SMS Notification Dialog -->
      <v-dialog v-model="showSMSDialog" persistent max-width="600px">
        <v-card>
          <v-card-title class="text-h5">SMS Benachrichtigung</v-card-title>
          <v-card-text>
            <div v-if="smsSendingState === 'preview'">
              <p class="subtitle-1">Möchtest du alle Teilnehmer personalisiert per SMS informieren?</p>

              <v-alert type="info" text dense border="left" class="mb-4">
                <strong>Beispiel-Nachricht:</strong><br />
                <span style="white-space: pre-line">{{ exampleMessage }}</span>
              </v-alert>

              <div class="subtitle-1 font-weight-bold mb-2">Empfänger ({{ smsRecipients.length }})</div>
              <v-list dense style="max-height: 200px; overflow-y: auto" class="grey lighten-5 rounded">
                <v-list-item v-for="(recipient, index) in smsRecipients" :key="index">
                  <v-list-item-content>
                    <v-list-item-title :class="{ 'red--text': !recipient.isValid, 'green--text': recipient.isValid }">
                      {{ recipient.name }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="d-flex align-center">
                      <span :class="{ 'font-weight-bold': recipient.wasModified }">{{ recipient.phoneNumber || "Keine Nummer" }}</span>

                      <v-tooltip bottom v-if="recipient.wasModified">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon x-small color="info" class="ml-1" v-bind="attrs" v-on="on">mdi-information</v-icon>
                        </template>
                        <span>Original: {{ recipient.originalPhone }}</span>
                      </v-tooltip>

                      <span v-if="!recipient.isValid" class="red--text font-weight-bold ml-2">Ungültig</span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-icon v-if="recipient.isValid" color="success" small>mdi-check</v-icon>
                    <v-icon v-else color="error" small>mdi-alert-circle</v-icon>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
              <div v-if="invalidRecipientsCount > 0" class="red--text caption mt-1">
                {{ invalidRecipientsCount }} Teilnehmer haben keine gültige Mobilnummer und erhalten keine SMS.
              </div>
            </div>

            <div v-if="smsSendingState === 'sending'" class="text-center py-4">
              <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
              <div class="mt-4">Sende SMS...</div>
            </div>

            <div v-if="smsSendingState === 'result'">
              <v-alert type="success" text>
                {{ smsResultSummary }}
              </v-alert>
              <div v-if="smsFailed.length > 0">
                <div class="subtitle-2 red--text">Fehlgeschlagen bei:</div>
                <ul>
                  <li v-for="fail in smsFailed" :key="fail.userId">{{ fail.name }} ({{ fail.error }})</li>
                </ul>
              </div>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <div v-if="smsSendingState === 'preview'">
              <v-btn text @click="showSMSDialog = false">Abbrechen</v-btn>
              <v-btn color="primary" @click="sendSMS" :disabled="smsRecipients.filter((r) => r.isValid).length === 0">
                <v-icon left>mdi-message-text</v-icon> SMS Senden ({{ smsRecipients.filter((r) => r.isValid).length }})
              </v-btn>
            </div>
            <div v-if="smsSendingState === 'result'">
              <v-btn color="primary" @click="showSMSDialog = false">Schliessen</v-btn>
            </div>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </v-dialog>
</template>

<script>
import api from "../../api"
import { mapActions } from "vuex"
import userSearch from "../../components/userSearch.vue"
import courseSearch from "../../components/courseSearch.vue"
export default {
  name: "courseListEdit",
  components: { userSearch, courseSearch },
  data() {
    return {
      loading: true,
      dialog: true,
      showCancelledPopup: false,
      showPushPopUp: false,
      courseEdit: null,
      time: "00:00:00",
      rooms: [],
      labels: [{ _id: null, text: "Kein Label" }],
      icons: [],
      events: [], // Beibehalten falls benötigt, im Template aber entfernt
      // SMS Logic
      showSMSDialog: false,
      smsRecipients: [],
      smsMessage: "",
      smsSendingState: "preview", // 'preview', 'sending', 'result'
      smsResultSummary: "",
      smsFailed: [],
    }
  },
  async created() {
    this.setNavigationBarText(null) // Wir verwenden jetzt die v-app-bar

    try {
      const [dbInfos, iconsData, eventsData] = await Promise.all([
        api.fetchCourseEdit(this.$route.params.id),
        api.fetchAllGFIcons(),
        api.fetchEventsForSelect(),
      ])

      this.rooms = dbInfos.rooms
      this.labels = this.labels.concat(dbInfos.labels)
      this.courseEdit = dbInfos.execution
      this.icons = iconsData.icons
      this.events = eventsData // Events-Daten sind verfügbar

      if (this.events.length) this.events.unshift({ _id: null, title: "Kein Event" })

      // Initialwerte setzen
      this.courseEdit.cancelledVisibility = this.courseEdit.cancelledVisibility || false
      this.courseEdit.room = this.courseEdit.room || null
      this.courseEdit.event = this.courseEdit.event || null
      this.courseEdit.additionalInfo = this.courseEdit.additionalInfo || ""
      this.courseEdit.icon = this.courseEdit.icon || "dumbell.json"

      const hours = String(this.courseEdit.hours).padStart(2, "0")
      const minutes = String(this.courseEdit.minutes).padStart(2, "0")
      this.time = `${hours}:${minutes}`

      this.loading = false
    } catch (error) {
      this.$toast.error("Kurs konnte nicht geladen werden.")
      this.closeDialog()
    }
  },
  methods: {
    ...mapActions("navigationStoreNotPersisted", ["setNavigationBarText"]),
    showPushPopupMethod() {
      if (this.courseEdit.instructors.includes(null)) {
        this.$toast.error("Bitte wähle für alle Felder einen Instruktor aus oder entferne die leeren Felder.")
        return // <-- Hier wird abgebrochen
      }
      this.showPushPopUp = true
    },
    cancelledCourseVisible(visible) {
      this.courseEdit.cancelledVisibility = visible
      this.showCancelledPopup = false
      // Trigger SMS dialog preparation after cancellation decision
      this.prepareSMSDialog()
    },

    async prepareSMSDialog() {
      this.smsSendingState = "preview"
      this.smsRecipients = []
      this.smsFailed = []
      this.smsResultSummary = ""

      // Extract day and month to save characters (e.g., "19.02.")
      const shortDate = new Date(this.courseEdit.ts * 1000).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit" })
      const time = this.$moment(this.courseEdit.ts * 1000)
        .hours(this.courseEdit.hours)
        .minutes(this.courseEdit.minutes)
        .format("HH:mm")

      const cleanForGSM7 = (text) => {
        return (
          text
            .replace(/®/g, "") // Registered Mark entfernen
            .replace(/©/g, "") // Copyright entfernen
            .replace(/™/g, "") // Trademark entfernen
            // Fallback: Alles, was nicht GSM-7-kompatibel ist, entfernen oder normalisieren
            .normalize("NFD")
            .replace(/[^\u0000-\u007FäöüÄÖÜß€\n\r]/g, "")
        )
      }
      const rawCourseTitle = this.courseEdit.course.name
      const courseTitle = cleanForGSM7(rawCourseTitle)

      try {
        const viewData = await api.fetchCourseView(this.courseEdit._id, 0)
        if (viewData?.courseView?.signups) {
          this.smsRecipients = viewData.courseView.signups.map((s) => {
            const user = s.user
            const rawPhone = user.telefon
            const cleanPhone = this.normalizePhoneNumber(rawPhone)
            const isValid = this.validatePhoneNumber(cleanPhone)

            let greeting = "Hallo"
            if (user.geschlecht === "m") greeting = "Lieber"
            if (user.geschlecht === "w") greeting = "Liebe"

            const fullName = `${user.vorname} ${user.nachname}`

            // Optimized message: ~135 characters base length.
            // Leaves ~25 characters buffer for long names/courses before hitting the 160 limit.
            const msgText = `${greeting} ${user.vorname}, der Kurs ${courseTitle} am ${shortDate} um ${time} Uhr fällt mangels Anmeldungen leider aus. Danke für dein Verständnis. WELL COME FIT Team`

            return {
              userId: user._id,
              name: fullName,
              phoneNumber: cleanPhone,
              originalPhone: rawPhone,
              wasModified: rawPhone !== cleanPhone && isValid,
              isValid: isValid,
              message: msgText,
            }
          })

          if (this.smsRecipients.length > 0) {
            this.showSMSDialog = true
          } else {
            this.$toast.info("Keine Teilnehmer angemeldet.")
          }
        }
      } catch (err) {
        console.error("Could not fetch participants for SMS", err)
        this.$toast.error("Fehler beim Laden der Teilnehmer für SMS.")
      }
    },

    async sendSMS() {
      this.smsSendingState = "sending"
      // Wir filtern nur valide Empfänger
      const validRecipients = this.smsRecipients.filter((r) => r.isValid)

      try {
        // Wir senden jetzt das Array mit den individuellen Messages
        const response = await api.sendCancellationSMS({
          courseId: this.courseEdit._id,
          recipients: validRecipients, // Hier sind jetzt die { message: "..." } Objekte drin
        })

        this.smsSendingState = "result"
        this.smsResultSummary = response.message

        // ... Error handling Code bleibt gleich ...
        if (response.results) {
          const failures = response.results.filter((r) => r.status === "failed")
          this.smsFailed = failures.map((f) => {
            const rec = this.smsRecipients.find((r) => r.userId === f.userId)
            return {
              userId: f.userId,
              name: rec ? rec.name : "Unknown",
              error: f.error,
            }
          })
        }
      } catch (e) {
        this.smsSendingState = "result"
        this.smsResultSummary = "Es ist ein Fehler aufgetreten beim Senden."
        console.error(e)
      }
    },
    validatePhoneNumber(phone) {
      if (!phone) return false
      // Simple validation: check if it has at least 10 digits/chars approx
      // E.164 format usually starts with +
      const cleaned = phone.replace(/[^\d+]/g, "")
      return cleaned.length >= 10 && cleaned.includes("+")
    },

    normalizePhoneNumber(phone) {
      if (!phone) return ""
      let cleaned = phone.trim()

      // Remove spaces
      cleaned = cleaned.replace(/\s/g, "")

      // Check for 00 start -> replace with +
      if (cleaned.startsWith("00")) {
        return "+" + cleaned.substring(2)
      }

      // Check for 0 start (and not 00) -> replace with +41
      if (cleaned.startsWith("0") && !cleaned.startsWith("00")) {
        return "+41" + cleaned.substring(1)
      }

      // If it starts with +, assume it's okay or at least intentional
      if (cleaned.startsWith("+")) {
        return cleaned
      }

      // Fallback: If no +, add +41 (unsafe assumption? usually correct for local context)
      // But user specifically asked for 076... -> +4176...
      return "+41" + cleaned
    },

    async saveUpdatedCourse(sendPush) {
      console.log("Instruktoren", this.courseEdit.instructors)
      this.showPushPopUp = false
      if (this.courseEdit.instructors.includes(null)) {
        this.$toast.error("Bitte wähle für alle Felder einen Instruktor aus oder entferne die leeren Felder.")
        return // <-- Hier wird abgebrochen
      }
      let updatedCourse = JSON.parse(JSON.stringify(this.courseEdit))
      updatedCourse.course = updatedCourse.course._id
      updatedCourse.sendPush = sendPush

      try {
        await api.updateCourseExecution(updatedCourse)
        this.$toast.success("Kurs erfolgreich aktualisiert!")
        this.$emit("updateCourse")
        this.closeDialog()
      } catch (e) {
        this.$toast.error("Speichern fehlgeschlagen.")
      }
    },

    changeCourseActivation() {
      this.courseEdit.cancelled = !this.courseEdit.cancelled
      if (this.courseEdit.cancelled) {
        this.showCancelledPopup = true
        // Prepare SMS Dialog but don't show it yet (wait for visibility decision or show immediately if preferred?)
        // The user requirement says: "wenn der Kurs abgesagt wird, eine checkbox / nachfrage an den benutzer machen, ob er direkt alle teilnehmer des kurses per SMS benachrichtigen möchte"
        // We'll hook into the cancelledCourseVisible method or add a secondary step.
      } else {
        this.courseEdit.cancelledVisibility = false
      }
    },

    removeInstructor(id) {
      console.log("Removing instructor with ID:", id)
      this.courseEdit.instructors = this.courseEdit.instructors.filter((instructorId) => instructorId !== id)
    },

    addInstructor() {
      if (!this.courseEdit.instructors.includes(null)) {
        this.courseEdit.instructors.push(null)
      }
    },

    updateCourse(newCourse) {
      this.courseEdit.course = newCourse
    },

    updateInstructor(newId, oldId) {
      if (oldId === null) {
        if (!this.courseEdit.instructors.includes(newId)) {
          this.courseEdit.instructors.pop()
          this.courseEdit.instructors.push(newId)
        } else {
          this.courseEdit.instructors.pop()
        }
      } else {
        const index = this.courseEdit.instructors.indexOf(oldId)
        if (index !== -1) {
          this.courseEdit.instructors.splice(index, 1, newId)
        }
      }
    },

    closeDialog() {
      this.dialog = false
      this.$nextTick(() => {
        this.$router.push("/kurse/" + this.courseEdit._id)
      })
    },
  },
  watch: {
    time(val) {
      if (val && this.courseEdit) {
        this.courseEdit.hours = parseInt(val.split(":")[0])
        this.courseEdit.minutes = parseInt(val.split(":")[1])
      }
    },
  },
  computed: {
    exampleMessage() {
      if (this.smsRecipients.length > 0 && this.smsRecipients[0].message) {
        return this.smsRecipients[0].message
      }
      return "Keine Vorschau verfügbar."
    },
    courseActivationText() {
      return this.courseEdit.cancelled ? "Kurs reaktivieren" : "Kurs absagen"
    },
    headerImageSrc() {
      return this.courseEdit?.course?.bild ? `/img/kurse/${this.courseEdit.course.bild}` : ""
    },
    invalidRecipientsCount() {
      return this.smsRecipients.filter((r) => !r.isValid).length
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .iconAvatar {
  width: 50px !important;
  height: 50px !important;
}
.course-edit-wrapper {
  background-color: #f7f8fa;
  height: 100vh;
  width: 100vw;
  overflow-y: auto;
}

.app-bar-border {
  border-bottom: 1px solid #e0e0e0 !important;
}

.form-container {
  padding-bottom: 24px;
  max-width: 700px;
}

.header-image-container {
  margin: -12px -12px 24px -12px;
  border-radius: 0 0 24px 24px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.form-card {
  border-radius: 16px;
  margin-bottom: 24px;
  border: 1px solid #e0e0e0;
}

::v-deep .v-chip .v-avatar .lottie-player {
  width: 32px !important;
  height: 32px !important;
}
</style>
